// src/modals/_PricingBlockCreateBody.jsx — Phase 6.B PR #167a (ADR-099) + PR #167b amend (ADR-100)
//
// Body extrait depuis PricingBlockCreateModal.jsx (refactor pur Option Hybride).
// Composant pur "body" : aucune logique de chrome (overlay), aucun Esc handler,
// aucun click-overlay. Le wrapper (PricingBlockCreateModal thin wrapper OU
// UnifiedDateActionModal) gère le chrome.
//
// State interne local (logementId, dates, reason, submitting, serverError,
// overlapResas, showResas). Reset state sur transition `open: false → true`
// via useEffect [open].
//
// PR #167b amend (ADR-100) — props `initialLogementId` + `initialStartDate`
// pour pré-fill au switch onglet depuis cellule cliquée. Le `<select>`
// logement reste éditable (pas locked). `onDirtyChange` notifie le wrapper
// dès qu'un champ est saisi/modifié (utilisé pour confirmation au switch tab).
//
// Préserve verbatim les textes (titre "🔒 Bloquer une période", messages
// d'erreur) pour préserver les selectors E2E `pricing-blocks.spec.js`.

import { useEffect, useState, useMemo } from "react";
import { Lock, ChevronDown, ChevronRight, User, CheckCircle, AlertTriangle, Loader2 } from "lucide-react";
import { DS } from "../lib/ui";
import { supabase } from "../supabase";
import { formatPeriodLabel } from "../tabs/calendar/_block-helpers";
import GuestFieldsForm from "./_GuestFieldsForm";  // PR #169a v1.4 (ADR-104)

/**
 * "YYYY-MM-DD" → lendemain, en UTC pur (pas de dérive d'heure d'été, pas de
 * concaténation de chaîne). Sert la valeur par défaut de la date de fin :
 * un blocage minimal VALIDE, c'est une nuit (end = start + 1 jour).
 * Entrée non conforme → "" (aucune date inventée).
 * @param {string} iso
 * @returns {string}
 */
export function _addOneDayIso(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d) + 86400000);
  const yy = shifted.getUTCFullYear();
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(shifted.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Body extrait Block create.
 * @param {object} props
 * @param {boolean} props.open — utilisé uniquement pour reset state à l'ouverture
 * @param {Array} props.allLogs — liste logements (filtrée côté wrapper si besoin)
 * @param {Map} props.modesByLogement — Map<logId, mode>
 * @param {boolean} props.dryRunActive
 * @param {string} [props.initialLogementId] — PR #167b amend : pré-fill logement
 * @param {string} [props.initialStartDate] — PR #167b amend : pré-fill dates
 *                                            (start = end = initialStartDate)
 * @param {function} [props.onDirtyChange] — PR #167b amend : notifié(isDirty:bool)
 *                                            à chaque évolution du form
 * @param {function} props.onClose — fermer la modal (bouton Annuler)
 * @param {function} props.onSaved — callback succès, signature ({ ok, ...json })
 */
export default function PricingBlockCreateBody({
  open,
  allLogs,
  modesByLogement,
  dryRunActive,
  initialLogementId,
  initialStartDate,
  onDirtyChange,
  onClose,
  onSaved,
  // 2026-09-15 : rafraîchir le calendrier quand le bloc existe SANS confirmation
  // Beds24 (502 avec block_id) — hissé par UnifiedDateActionModal (BPRa-2).
  invalidate,
}) {
  // ═════════════════════════════════════════════════════════════════════════
  // 🔴 LE FORMULAIRE S'OUVRAIT DÉJÀ EN ERREUR — corrigé le 2026-08-05
  // ═════════════════════════════════════════════════════════════════════════
  // `endDate` était initialisée à la MÊME date que `startDate`. Or la règle de
  // validation (Convention A, ADR-111) exige `endDate > startDate` — une
  // journée seule n'est pas une nuit. Conséquence : en cliquant sur une date du
  // calendrier pour la bloquer, l'utilisateur tombait sur un message d'erreur
  // rouge et un bouton « Bloquer » grisé, AVANT d'avoir rien saisi. Il fallait
  // deviner qu'il fallait avancer la date de fin d'un jour.
  //
  // La valeur par défaut est désormais le blocage minimal VALIDE : une nuit,
  // soit `startDate + 1 jour` — exactement ce que dit la convention.
  const initialEndDate = initialStartDate ? _addOneDayIso(initialStartDate) : "";
  const [logementId, setLogementId] = useState(initialLogementId || "");
  const [startDate, setStartDate] = useState(initialStartDate || "");
  const [endDate, setEndDate] = useState(initialEndDate);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [overlapResas, setOverlapResas] = useState(null);
  const [showResas, setShowResas] = useState(false);
  // PR #169a v1.4 (ADR-104) — section voyageur repliable (résa direct interne FBZ).
  // Si guest_first_name rempli au submit → pricing_block AVEC voyageur (résa direct
  // interne). Sinon → blocage simple (rétrocompat totale).
  const [showGuestSection, setShowGuestSection] = useState(false);
  const [guestFields, setGuestFields] = useState({});

  // Reset state à chaque ouverture (transition `open: false → true`).
  // PR #167b amend : pré-fill depuis initialLogementId/initialStartDate si
  // fournis. Si user a switché tab depuis Prix avec date sélectionnée,
  // le form Bloquer démarre pré-rempli sur la même date + logement.
  useEffect(() => {
    if (open) {
      setLogementId(initialLogementId || "");
      setStartDate(initialStartDate || "");
      // Même valeur par défaut qu'au premier montage : une nuit (start + 1).
      // Sans cette ligne, la 2e ouverture de la modale rouvrirait en erreur.
      setEndDate(initialEndDate);
      setReason("");
      setSubmitting(false);
      setServerError(null);
      setOverlapResas(null);
      setShowResas(false);
      // PR #169a v1.4 — reset section voyageur à chaque ouverture.
      setShowGuestSection(false);
      setGuestFields({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLogementId, initialStartDate]);

  // PR #167b amend — Dirty detection : form considéré dirty dès qu'un champ
  // diverge des initial props OU dès qu'une raison est saisie. Permet au
  // wrapper de demander confirmation avant switch tab.
  // PR #169a v1.4 — ajout section voyageur (au moins un champ rempli).
  useEffect(() => {
    if (!onDirtyChange) return;
    const guestDirty = Object.values(guestFields).some(
      (v) => v != null && v !== "",
    );
    const isDirty =
      logementId !== (initialLogementId || "") ||
      startDate !== (initialStartDate || "") ||
      // Comparé à la NOUVELLE valeur par défaut (start + 1 jour) : sinon le
      // formulaire se déclarerait « modifié » dès son ouverture et demanderait
      // une confirmation avant fermeture, sans que rien n'ait été saisi.
      endDate !== initialEndDate ||
      reason !== "" ||
      guestDirty;
    onDirtyChange(isDirty);
  }, [
    logementId,
    startDate,
    endDate,
    reason,
    guestFields,
    initialLogementId,
    initialStartDate,
    initialEndDate,
    onDirtyChange,
  ]);

  // BPR5-3 v1.2 : filter 'p6' (Maison Zen entier non bloquable).
  // Filter logements ayant pricing_mode défini (sinon backend rejette).
  const availableLogs = useMemo(() => {
    if (!Array.isArray(allLogs)) return [];
    return allLogs
      .filter((l) => l.id !== "p6")
      .filter((l) => modesByLogement?.get?.(l.id) != null);
  }, [allLogs, modesByLogement]);

  // C51 ADR-111 — Convention A : end_date STRICTEMENT après start_date
  // (1 nuit = end = start + 1 jour). end == start refusé (data pourrie).
  const datesValid =
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(endDate) &&
    endDate > startDate;
  const reasonValid = reason.length <= 200;
  const formValid = !!logementId && datesValid && reasonValid;

  async function handleSubmit() {
    if (!formValid || submitting) return;
    setSubmitting(true);
    setServerError(null);
    setOverlapResas(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setServerError("Non authentifié");
        setSubmitting(false);
        return;
      }
      // PR #169a v1.4 — body inclut guest_fields si section voyageur active et
      // guest_first_name rempli. Sinon body inchangé (rétrocompat blocage simple).
      const hasGuest =
        showGuestSection &&
        guestFields.guest_first_name &&
        guestFields.guest_first_name.trim() !== "";
      const body = {
        logement_id: logementId,
        start_date: startDate,
        end_date: endDate,
        reason: reason || null,
      };
      if (hasGuest) {
        body.guest_fields = { ...guestFields };
      }
      const res = await fetch(
        "/api/beds24-mapping?action=pricing_block_create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(body),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (res.status === 409 && json.error === "reservation_overlap") {
        // D-3 REFUS DUR — liste des résas chevauchantes.
        setOverlapResas(json.reservations || []);
        setServerError(
          `Refus : ${json.reservations?.length || 0} réservation(s) active(s) sur cette période.`,
        );
        setSubmitting(false);
        return;
      }
      if (res.status === 409 && json.error === "block_overlap") {
        setServerError(
          "Période déjà bloquée pour ce logement (chevauchement) — ce blocage existe déjà chez vous. S'il n'est pas confirmé chez Beds24 (badge ⟳ au calendrier), ouvrez-le et cliquez « Pousser maintenant ».",
        );
        setSubmitting(false);
        return;
      }
      if (
        res.status === 400 &&
        json.error === "logement_id_invalid"
      ) {
        const detail =
          json.reason === "logement_p6_not_blockable_use_subloc"
            ? "Maison Zen entier non bloquable — utilisez une sous-loc (Cocon Naturel / lc2 / Sur un Nuage)."
            : "Format logement invalide.";
        setServerError(detail);
        setSubmitting(false);
        return;
      }
      if (res.status === 400 && json.error === "start_date_past") {
        setServerError(
          `Date début passée (today Paris = ${json.today_paris || "?"}).`,
        );
        setSubmitting(false);
        return;
      }
      if (!res.ok) {
        setServerError(json.message || json.error || `http_${res.status}`);
        // 2026-09-15 (pousseur « blocage manuel ») : un 502 qui porte block_id =
        // le bloc EXISTE chez FBZ (créé par la RPC avant le push) mais Beds24 n'a
        // pas confirmé (refus ou injoignable). On rafraîchit le calendrier pour
        // qu'il le montre tel qu'il est (« Pousser maintenant » dans sa fiche),
        // SANS passer par onSaved — dont le toast dirait « Période bloquée
        // Beds24 ✅ », exactement le mensonge qu'on vient de fermer.
        if (res.status === 502 && json.block_id) {
          try { invalidate && invalidate(); } catch (_e) { /* best-effort */ }
        }
        setSubmitting(false);
        return;
      }
      onSaved && onSaved({ ok: true, ...json });
    } catch (e) {
      setServerError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  // PR #167 — label nuits factorisé dans formatPeriodLabel (_block-helpers).
  // Partagé avec PricingBlockDetailsModal pour cohérence UX.
  const periodLabel = formatPeriodLabel({ startDate, endDate });

  return (
    <div data-testid="pricing-block-create-body">
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontFamily: DS.fontDisplay,
            fontSize: 18,
            fontWeight: 600,
            color: DS.marineDark,
          }}
        >
          <Lock size={18} strokeWidth={2.2} /> Bloquer une période
        </div>
        <div style={{ fontSize: 12, color: DS.textSecondary, marginTop: 6 }}>
          Marquer une période comme indisponible (travaux, visite proprio,
          indispo). Push Beds24 <code>numAvail=0</code> sur cette période.
        </div>
      </div>

      {/* Select logement (BPR5-3 filter p6) */}
      <div style={{ marginBottom: 10 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            color: DS.navy,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Logement *
        </label>
        <select
          value={logementId}
          onChange={(e) => setLogementId(e.target.value)}
          disabled={submitting}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: `0.5px solid ${DS.borderHover}`,
            fontSize: 13,
            fontFamily: "inherit",
            boxSizing: "border-box",
            background: "#fff",
          }}
        >
          <option value="">— Choisir —</option>
          {availableLogs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.id} · {l.nom}
            </option>
          ))}
        </select>
        {availableLogs.length === 0 && (
          <div style={{ color: DS.red, fontSize: 11, marginTop: 3 }}>
            Aucun logement configuré pour pricing (cf. Paramètres &gt;
            Beds24 &gt; Pricing config).
          </div>
        )}
      </div>

      {/* Dates */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 700,
              color: DS.navy,
              marginBottom: 4,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            Date début *
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={submitting}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              border: `0.5px solid ${DS.borderHover}`,
              fontSize: 13,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 700,
              color: DS.navy,
              marginBottom: 4,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            Date fin *
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={submitting}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 6,
              border: `0.5px solid ${DS.borderHover}`,
              fontSize: 13,
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>
      </div>
      {startDate && endDate && !datesValid && (
        <div
          data-testid="pricing-block-dates-error"
          style={{ color: DS.red, fontSize: 11, marginTop: -4, marginBottom: 8 }}
        >
          Date fin doit être APRÈS la date début (1 nuit = end = start + 1 jour).
        </div>
      )}
      <div style={{ fontSize: 11, color: DS.textSecondary, marginBottom: 10 }}>
        {periodLabel}
      </div>

      {/* Reason facultatif */}
      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            color: DS.navy,
            marginBottom: 4,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Raison (facultatif)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="ex: travaux peinture salon"
          maxLength={200}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 6,
            border: `0.5px solid ${DS.borderHover}`,
            fontSize: 13,
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* PR #169a v1.4 (ADR-104) — Section repliable "Détails voyageur" :
          si admin remplit guest_first_name → résa direct interne FBZ.
          Sinon → blocage simple (rétrocompat totale). Aucun push Beds24. */}
      <div style={{ marginBottom: 14 }}>
        <button
          type="button"
          data-testid="pricing-block-guest-section-toggle"
          onClick={() => setShowGuestSection((v) => !v)}
          disabled={submitting}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            background: "transparent",
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "8px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            width: "100%",
            textAlign: "left",
          }}
        >
          {showGuestSection ? <ChevronDown size={14} /> : <ChevronRight size={14} />} <User size={14} strokeWidth={2.2} /> Détails voyageur (optionnel —
          résa direct interne FBZ)
        </button>
        {showGuestSection && (
          <div
            data-testid="pricing-block-guest-section"
            style={{
              marginTop: 8,
              padding: 12,
              background: DS.cielSoft,
              borderRadius: DS.radiusControl,
              border: `1px solid ${DS.border}`,
            }}
          >
            <GuestFieldsForm
              values={guestFields}
              onChange={(key, val) =>
                setGuestFields((prev) => ({ ...prev, [key]: val }))
              }
              disabled={submitting}
            />
          </div>
        )}
      </div>

      {/* DRY_RUN indication */}
      {dryRunActive ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            background: DS.successBg,
            color: DS.successText,
            border: `1px solid ${DS.success}55`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <CheckCircle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>Mode DRY_RUN ACTIF : aucun push réel Beds24. Le block sera
          créé en base et tracé dans <code>pricing_audit_log</code>.</span>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: 10,
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `1px solid ${DS.danger}44`,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={14} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} /><span>DRY_RUN OFF : push <strong>RÉEL</strong> Beds24
          (numAvail=0). Les OTAs marketplace verront la période comme
          indisponible.</span>
        </div>
      )}

      {/* Error display + résas chevauchantes (D-3) */}
      {serverError && (
        <div
          style={{
            padding: 10,
            background: DS.dangerBg,
            color: DS.dangerText,
            border: `1px solid ${DS.danger}44`,
            borderRadius: 6,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><AlertTriangle size={13} strokeWidth={2.2} /> {serverError}</span>
          {overlapResas && overlapResas.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowResas((v) => !v)}
                style={{
                  background: "transparent",
                  color: DS.navy,
                  border: "1px solid " + DS.navy,
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {showResas
                  ? "Masquer les résas"
                  : `Voir résas concernées (${overlapResas.length})`}
              </button>
              {showResas && (
                <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 11 }}>
                  {overlapResas.map((r) => (
                    <li key={r.id} style={{ marginBottom: 4 }}>
                      <strong>{r.guest_name || "—"}</strong> · {r.source} ·{" "}
                      {r.dates}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          className="fbz-press"
          onClick={onClose}
          disabled={submitting}
          style={{
            background: DS.card,
            color: DS.marine,
            border: `0.5px solid ${DS.borderHover}`,
            borderRadius: DS.radiusControl,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          className="fbz-press"
          onClick={handleSubmit}
          disabled={!formValid || submitting}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: DS.corail,
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: !formValid || submitting ? "not-allowed" : "pointer",
            opacity: !formValid || submitting ? 0.5 : 1,
          }}
        >
          {submitting
            ? <><Loader2 size={13} /> Envoi…</>
            : dryRunActive
              ? "Bloquer (dry_run)"
              : "Bloquer la période"}
        </button>
      </div>
    </div>
  );
}
